import React, { createContext, useContext, useState, useCallback } from 'react';
import { toast } from 'sonner';

const SimuladorContext = createContext(null);

export function SimuladorProvider({ children }) {
  const [operadoresAtivos, setOperadoresAtivos] = useState([]); // [{id, nome, username}]
  const [isMinimizado, setIsMinimizado] = useState(false);
  const [isAberto, setIsAberto] = useState(false);
  const [gridCols, setGridCols] = useState(2);
  const [fullscreenOperador, setFullscreenOperador] = useState(null);

  const adicionarOperador = useCallback((operador) => {
    if (operadoresAtivos.length >= 10) {
      toast.error('Máximo de 10 operadores em simultâneo');
      return false;
    }
    if (operadoresAtivos.find(o => o.id === operador.id)) {
      toast.warning('Operador já está aberto');
      return false;
    }
    setOperadoresAtivos(prev => [...prev, {
      id: operador.id,
      nome: operador.nome,
      username: operador.username
    }]);
    setIsAberto(true);
    setIsMinimizado(false);
    // Toast na posição bottom-center para não sobrepor botões
    toast.success(`${operador.nome || operador.username} adicionado ao simulador`, {
      position: 'bottom-center',
      duration: 2000
    });
    return true;
  }, [operadoresAtivos]);

  const removerOperador = useCallback((operadorId) => {
    setOperadoresAtivos(prev => prev.filter(o => o.id !== operadorId));
    if (fullscreenOperador === operadorId) {
      setFullscreenOperador(null);
    }
  }, [fullscreenOperador]);

  const removerTodos = useCallback(() => {
    setOperadoresAtivos([]);
    setFullscreenOperador(null);
    setIsAberto(false);
  }, []);

  const toggleMinimizar = useCallback(() => {
    setIsMinimizado(prev => !prev);
  }, []);

  const abrirSimulador = useCallback(() => {
    setIsAberto(true);
    setIsMinimizado(false);
  }, []);

  const fecharSimulador = useCallback(() => {
    setIsAberto(false);
    setIsMinimizado(false);
  }, []);

  const toggleFullscreen = useCallback((operadorId) => {
    setFullscreenOperador(prev => prev === operadorId ? null : operadorId);
  }, []);

  const recarregarOperador = useCallback((operadorId) => {
    const iframe = document.getElementById(`iframe-sim-${operadorId}`);
    if (iframe) {
      iframe.src = iframe.src;
    }
  }, []);

  const value = {
    operadoresAtivos,
    isMinimizado,
    isAberto,
    gridCols,
    fullscreenOperador,
    setGridCols,
    adicionarOperador,
    removerOperador,
    removerTodos,
    toggleMinimizar,
    abrirSimulador,
    fecharSimulador,
    toggleFullscreen,
    recarregarOperador
  };

  return (
    <SimuladorContext.Provider value={value}>
      {children}
    </SimuladorContext.Provider>
  );
}

export function useSimulador() {
  const context = useContext(SimuladorContext);
  if (!context) {
    throw new Error('useSimulador deve ser usado dentro de SimuladorProvider');
  }
  return context;
}
